import type { ScotomaSpot } from "shared/schema/src/types";

// Scotoma spot radii for S, M, L
export const SCOTOMA_RADII = { S: 30, M: 60, L: 100 };

/**
 * Apply scotoma (blind spot) effects to image data
 * @param imageData - ImageData to modify
 * @param spots - Array of scotoma spots to apply
 * @returns Modified ImageData
 */
export function applyScotomaToImageData(
	imageData: ImageData,
	spots: ScotomaSpot[],
): ImageData {
	const data = imageData.data;
	const width = imageData.width;
	const height = imageData.height;

	for (const spot of spots) {
		const radius = spot.radius;
		const centerX = spot.x;
		const centerY = spot.y;

		// Calculate bounds for optimization
		const minX = Math.max(0, Math.floor(centerX - radius));
		const maxX = Math.min(width - 1, Math.ceil(centerX + radius));
		const minY = Math.max(0, Math.floor(centerY - radius));
		const maxY = Math.min(height - 1, Math.ceil(centerY + radius));

		for (let y = minY; y <= maxY; y++) {
			for (let x = minX; x <= maxX; x++) {
				const distance = Math.sqrt((x - centerX) ** 2 + (y - centerY) ** 2);

				if (distance <= radius) {
					// Opacity = the spot's intensity at the center, fading to 0 at the
					// edge. (Pre-F7 this was hardcoded 0.5, ignoring spot.intensity.)
					const opacity = spot.intensity * (1 - distance / radius);

					const idx = (y * width + x) * 4;
					const r = data[idx];
					const g = data[idx + 1];
					const b = data[idx + 2];

					if (r !== undefined && g !== undefined && b !== undefined) {
						// Apply scotoma effect: darken the pixel
						data[idx] = Math.max(0, r * (1 - opacity)); // Red
						data[idx + 1] = Math.max(0, g * (1 - opacity)); // Green
						data[idx + 2] = Math.max(0, b * (1 - opacity)); // Blue
						// Alpha channel remains unchanged
					}
				}
			}
		}
	}

	return imageData;
}
